import Breadcrumb from "@/components/Breadcrumb";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import FlaggedPartTable from "./FlaggedPartTable";
import {
  useChangeToAdminFlagged,
  useGetFlaggedParts,
} from "@/hooks/flaggedPartHooks";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { useEffect, useMemo, useState } from "react";
import type { flagPartParam } from "@/api/flaggedPartService";
import {
  DefaultPrimaryButton,
  DefaultSecondaryButton,
} from "@/styles/globalstyles";
import { useUserInfo } from "@/context/UserInfoContext";
import UnFlaggedModal from "@/components/flagged-part/UnFlaggedModal";
import UnFlagAllModal from "@/components/flagged-part/UnFlagAllModal";
import { FlagOutlined } from "@mui/icons-material";
import {
  ConfirmAlert,
  ErrorAlert,
  WarningAlert,
} from "@/utils/alert";
import { useToast } from "@/hooks/useToast";
import { usePermissions } from "@/hooks/usePermissions";
import { useSearchParams } from "react-router-dom";

const FlaggedPart = () => {
  const canEdit = usePermissions().canEdit("flaggedpart");
  const userInfo = useUserInfo();
  const { showToast } = useToast();
  const isAdminFlagged =
    userInfo?.groups?.some((g) => g.trim().toLowerCase() === "pu admin") ||
    false;
  const [filterParam, setFilterParam] = useState<flagPartParam>({
    skip: 0,
    limit: 10,
    approval_id: isAdminFlagged ? undefined : userInfo?.user_id,
  });
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [searchBuyerId, setSearchBuyerId] = useState<string>("");
  const [onlyApproved, setOnlyApproved] = useState<boolean>(true);
  const [isFlagged, setIsFlagged] = useState<string>("");
  const [isOpenUnFlagModal, setIsOpenUnFlagModal] = useState<boolean>(false);
  const [isOpenUnflagAllModal, setIsOpenUnflagAllModal] =
    useState<boolean>(false);
  const [selectedParts, setSelectedParts] = useState<
    {
      partId: number;
      approvalId?: string;
    }[]
  >([]);
  const [isOpenSingleUnFlagModal, setIsOpenSingleUnFlagModal] =
    useState<boolean>(false);
  const [singleUnFlagPart, setSingleUnFlagPart] = useState<{
    partId: number;
    approvalId?: string;
  } | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(
    () => Number(searchParams.get("page")) || 0,
    [searchParams],
  );
  const rowsPerPage = useMemo(
    () => Number(searchParams.get("limit")) || 10,
    [searchParams],
  );

  useEffect(() => {
    const search = searchParams.get("search") || "";
    const buyerId = searchParams.get("buyerId") || "";
    const onlyApprovedParam =
      (searchParams.get("onlyApproved") ?? "true") === "true";
    const isFlaggedParam = searchParams.get("isFlagged") ?? "";
    setSearchFilter(search);
    setSearchBuyerId(buyerId);
    setOnlyApproved(onlyApprovedParam);
    setIsFlagged(isFlaggedParam);
    const newFilterParam: flagPartParam = {
      search_filter: search ? search : undefined,
      buyer_id: buyerId ? buyerId : undefined,
      approval_id: onlyApprovedParam ? userInfo?.user_id : undefined,
      is_flagged: isFlaggedParam
        ? isFlaggedParam === "true"
          ? true
          : false
        : undefined,
    };
    setFilterParam(newFilterParam);
  }, [searchParams, userInfo?.user_id]);

  const {
    data: flaggedParts,
    isFetching: isFlaggedPartsLoading,
    isError: isFlaggedPartsError,
  } = useGetFlaggedParts({
    ...filterParam,
    is_admin_flagged: isAdminFlagged,
    skip: page * rowsPerPage,
    limit: rowsPerPage,
  });

  // ต้องอ้างอิง filterParam ที่ผ่านการกด "ค้นหา" แล้วเท่านั้น ไม่ใช่ onlyApproved
  // checkbox ตรงๆ — ไม่งั้นแค่ติ๊ก checkbox โดยยังไม่กดค้นหา ปุ่มจะ enable ก่อนที่
  // filter ที่ใช้จริงตอนยิง unflag-all จะถูก scope ด้วย approval_id จริง
  //
  // ห้าม bypass ให้ admin ข้ามเงื่อนไขนี้ — filter_flagged_parts (backend) จะใส่
  // เงื่อนไข fp.is_admin_flagged=1 ก็ต่อเมื่อ approval_id ไม่ว่างเท่านั้น ถ้า
  // approval_id ว่าง (ไม่ติ๊ก checkbox) แม้ is_admin_flagged=true จะถูกส่งไป
  // query จะไม่ถูก scope เลย (กวาดทั้ง buyer-flagged และ admin-flagged ปนกัน)
  // เพราะฉะนั้น admin ก็ต้องติ๊ก checkbox ก่อนเช่นกัน
  const canUnflagAll = canEdit && !!filterParam.approval_id;

  // "ปลด Flagged ทั้งหมด" must always target only currently-flagged parts,
  // regardless of what the "สถานะการถูก Flag" dropdown is set to — so this
  // count is fetched with is_flagged forced true, independent of filterParam.
  const { data: matchingFlaggedParts } = useGetFlaggedParts(
    {
      ...filterParam,
      is_admin_flagged: isAdminFlagged,
      is_flagged: true,
      limit: 1,
    },
    canUnflagAll,
  );
  const matchingFlaggedCount = matchingFlaggedParts?.total ?? 0;

  const {
    mutate: mutateChangeToAdminFlagged,
    isPending: isChangeToAdminFlaggedPending,
  } = useChangeToAdminFlagged();

  useQueryHandler({
    isError: isFlaggedPartsError,
    isFetching: isChangeToAdminFlaggedPending,
  });

  useEffect(() => {
    document.title = "Flagged Part | AMPA";
  }, []);

  const onPageChange = (_event: unknown, newPage: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", String(newPage));
      return newParams;
    }, { replace: true });
  };

  const onRowPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("limit", event.target.value);
      newParams.set("page", "0");
      return newParams;
    }, { replace: true });
  };

  const handleSearch = () => {
    const newFilterParam: flagPartParam = {
      search_filter: searchFilter ? searchFilter : undefined,
      buyer_id: searchBuyerId ? searchBuyerId : undefined,
      approval_id: onlyApproved ? userInfo?.user_id : undefined,
      is_flagged: isFlagged ? (isFlagged === "true" ? true : false) : undefined,
    };
    setFilterParam(newFilterParam);
    setSelectedParts([]);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", String(0));
      if (searchFilter) {
        newParams.set("search", searchFilter);
      } else {
        newParams.delete("search");
      }
      if (searchBuyerId) {
        newParams.set("buyerId", searchBuyerId);
      } else {
        newParams.delete("buyerId");
      }
      if (onlyApproved === true || onlyApproved === false) {
        newParams.set("onlyApproved", String(onlyApproved));
      } else {
        newParams.delete("onlyApproved");
      }
      if (isFlagged) {
        newParams.set("isFlagged", isFlagged);
      } else {
        newParams.delete("isFlagged");
      }
      return newParams;
    }, { replace: true });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleUnFlag = (partId?: number, approvalId?: string) => {
    if (partId) {
      setSingleUnFlagPart({
        partId: partId,
        approvalId: approvalId || "",
      });
      setIsOpenSingleUnFlagModal(true);
    } else {
      setIsOpenUnFlagModal(true);
    }
  };

  const handleChangeToAdminFlagged = () => {
    const partIds: number[] = selectedParts.map((part) => part.partId);
    mutateChangeToAdminFlagged(partIds, {
      onSuccess: () => {
        setSelectedParts([]);
        showToast("ปรับสถานะเป็น Admin Flagged เรียบร้อยแล้ว", "success");
      },
      onError: (error) => {
        console.error("Error changing to admin flagged:", error);
        ErrorAlert("Error", "เกิดข้อผิดพลาดในการปรับสถานะเป็น Admin Flagged");
      },
    });
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb current="flaggedpart" />
      <Box mt={1}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              minWidth: "300px",
              width: "auto",
              gap: 1,
            }}
          >
            <Button
              size="small"
              sx={{ mt: 2, mb: 1, ...DefaultSecondaryButton }}
              variant="outlined"
              disabled={
                !selectedParts.length || selectedParts.length === 0 || !canEdit
              }
              onClick={() => {
                const checkIsAllFlagged = flaggedParts?.data
                  .filter((part) =>
                    selectedParts?.some((sel) => sel.partId === part.part_id),
                  )
                  .every((part) => part.is_flagged);
                if (!checkIsAllFlagged) {
                  WarningAlert(
                    "Warning",
                    "กรุณาเลือกเฉพาะรายการที่ถูก Flag เท่านั้น",
                  );
                } else {
                  handleUnFlag();
                }
              }}
            >
              <FlagOutlined /> ปลด Flag
            </Button>
            {isAdminFlagged && (
              <Button
                size="small"
                sx={{
                  mt: 2,
                  mb: 1,
                  ...DefaultSecondaryButton,
                  borderColor: "success.light",
                  color: "success.light",
                }}
                variant="outlined"
                disabled={
                  !selectedParts.length ||
                  selectedParts.length === 0 ||
                  !canEdit ||
                  !isAdminFlagged
                }
                onClick={() => {
                  const checkIsAllFlagged = flaggedParts?.data
                    .filter((part) =>
                      selectedParts?.some((sel) => sel.partId === part.part_id),
                    )
                    .every((part) => part.is_flagged && !part.is_admin_flagged);
                  if (!checkIsAllFlagged) {
                    WarningAlert(
                      "Warning",
                      "กรุณาเลือกเฉพาะรายการที่เป็น Buyer Flagged เท่านั้น",
                    );
                  } else {
                    ConfirmAlert(
                      "Confirm",
                      "คุณต้องการปรับสถานะเป็น Admin Flagged ใช่หรือไม่?",
                      "ยืนยัน",
                      "ยกเลิก",
                    ).then((result) => {
                      if (result) {
                        handleChangeToAdminFlagged();
                      }
                    });
                  }
                }}
              >
                <FlagOutlined /> ปรับ Flag
              </Button>
            )}
            <Button
              size="small"
              sx={{ mt: 2, mb: 1, ...DefaultSecondaryButton }}
              variant="outlined"
              disabled={!canUnflagAll || matchingFlaggedCount === 0}
              onClick={() => setIsOpenUnflagAllModal(true)}
            >
              <FlagOutlined /> ปลด Flag ทั้งหมด
            </Button>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              fontSize="0.9rem"
              width="100px"
              mt={2}
              mb={1}
              mr={1}
            >
              Selected {selectedParts.length} items
            </Typography>
            <TextField
              label="ค้นหา Part No. / Part Name / 1st Tier Supplier"
              variant="outlined"
              size="small"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              sx={{
                mt: 2,
                mb: 1,
                width: "25%",
                "& .MuiInputLabel-root": {
                  fontSize: "0.8rem",
                  color: "#666666",
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#cccccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#999999",
                  },
                },
              }}
              onKeyDown={handleSearchKeyDown}
            />
            <TextField
              label="ค้นหา Buyer ID"
              variant="outlined"
              size="small"
              value={searchBuyerId}
              onChange={(e) => setSearchBuyerId(e.target.value)}
              sx={{
                mt: 2,
                mb: 1,
                width: "13.5%",
                "& .MuiInputLabel-root": {
                  fontSize: "0.8rem",
                  color: "#666666",
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#cccccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#999999",
                  },
                },
              }}
              onKeyDown={handleSearchKeyDown}
            />
            <FormControl size="small" sx={{ width: "15%", mt: 2, mb: 1 }}>
              <InputLabel
                id="is-flagged-label"
                sx={{
                  fontSize: "0.8rem",
                  color: "#666666",
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                }}
              >
                สถานะการถูก Flag
              </InputLabel>
              <Select
                labelId="is-flagged-label"
                value={isFlagged}
                onChange={(e) => setIsFlagged(e.target.value)}
                label="สถานะการถูก Flag"
              >
                {[
                  <MenuItem key="all" value="">
                    <Typography>ทั้งหมด</Typography>
                  </MenuItem>,
                  <MenuItem key="user" value="true">
                    <Typography>Flagged</Typography>
                  </MenuItem>,
                  <MenuItem key="system" value="false">
                    <Typography>Non Flagged</Typography>
                  </MenuItem>,
                ]}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyApproved}
                  onChange={() => {
                    setOnlyApproved((prev) => !prev);
                  }}
                />
              }
              label={"แสดงเฉพาะรายการที่ฉันต้องอนุมัติ"}
              sx={{
                width: "auto",
                ml: 0.25,
                mt: 2,
                mb: 1,
                "& .MuiFormControlLabel-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
            <Button
              size="small"
              variant="contained"
              sx={{ mt: 2, mb: 1, ...DefaultPrimaryButton }}
              onClick={handleSearch}
            >
              ค้นหา
            </Button>
          </Box>
        </Box>
        <FlaggedPartTable
          rows={flaggedParts?.data || []}
          count={flaggedParts?.total || 0}
          isLoading={isFlaggedPartsLoading}
          page={page}
          rowPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowPerPageChange={onRowPerPageChange}
          setSelectedParts={setSelectedParts}
          selectedParts={selectedParts}
          handleUnFlag={handleUnFlag}
          canEdit={canEdit}
        />
      </Box>

      <UnFlaggedModal
        open={isOpenSingleUnFlagModal}
        selectedParts={singleUnFlagPart ? [singleUnFlagPart] : []}
        handleClose={() => setIsOpenSingleUnFlagModal(false)}
        clear={() => setSingleUnFlagPart(null)}
      />

      <UnFlaggedModal
        open={isOpenUnFlagModal}
        selectedParts={selectedParts}
        handleClose={() => setIsOpenUnFlagModal(false)}
        clear={() => setSelectedParts([])}
      />

      <UnFlagAllModal
        open={isOpenUnflagAllModal}
        filter={filterParam}
        isAdminFlagged={isAdminFlagged}
        affectedCount={matchingFlaggedCount}
        handleClose={() => setIsOpenUnflagAllModal(false)}
        clear={() => {
          setSelectedParts([]);
          setSearchParams(
            (prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set("page", "0");
              return newParams;
            },
            { replace: true },
          );
        }}
      />
    </Box>
  );
};

export default FlaggedPart;
